'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import { ImagePlus, Heart, Plus } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { AIGlowBorder } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import type { Concept } from '@/types/concept';
import type { HistoryItem } from '@/features/design/history';
import { ConceptActivityOverlay } from '@/features/design/concept';
import { useActiveJobForConcept } from '@/features/design/job-queue';
import { RenderStatus } from '@/shared/constants/status';
import {
  SectionHeader,
  SectionPanel,
  ScrollableCarousel,
  PANEL_HEIGHT,
  CARD_ASPECT_RATIO,
  type SectionAction,
} from './section-components';
import { OnboardingEmptyState } from './OnboardingEmptyState';

// Concept Thumbnail
interface ConceptThumbnailProps {
  concept: Concept | HistoryItem;
  projectId: string;
  isNew?: boolean;
}

function ConceptThumbnail({
  concept,
  projectId,
  isNew,
}: ConceptThumbnailProps) {
  const t = useTranslations('render.queue');
  const attrs = concept.attributes;
  const mainRenderUrl =
    'main_render_url' in attrs ? attrs.main_render_url : null;
  const mainAngleUrl = 'main_angle_url' in attrs ? attrs.main_angle_url : null;
  const imageUrl =
    mainRenderUrl ||
    mainAngleUrl ||
    ('image_url' in attrs ? attrs.image_url : null);
  const hasNoRender = !mainRenderUrl && !('image_url' in attrs && attrs.image_url);
  const sequenceNumber =
    (attrs as { project_sequence_number?: number }).project_sequence_number ??
    0;
  const title = 'title' in attrs ? attrs.title : null;
  const name = attrs.name;
  const isFavorite = attrs.is_favorite;
  // Active job for the concept — any angle, any type (render or album).
  // Concept card represents the entire concept, so progress UI must cover ANY
  // running job: main angle render, derivative angle render, Nth render of a
  // derivative angle, or album generation. Multiple jobs may run simultaneously,
  // so we show an indeterminate spinner (no percentage), matching Apple's
  // `anyOverlayJob(forConceptId:)` + indeterminate ProgressView and Android's
  // `ConceptGenerationOverlay` spinner.
  const activeRenderFromQueue = useActiveJobForConcept(concept.id);

  const activeRenderFromAttrs = 'activerender' in attrs
    ? (attrs as { activerender?: { id: string; status: string } }).activerender ?? null
    : null;

  const activeRenderStatus =
    activeRenderFromQueue?.status ?? activeRenderFromAttrs?.status ?? null;

  const isJobActive =
    activeRenderStatus === RenderStatus.PENDING ||
    activeRenderStatus === RenderStatus.PROCESSING;
  const isJobFailed = activeRenderStatus === RenderStatus.FAILED;
  const hideOverlayBadges = isJobActive || isJobFailed;
  const isDesaturated = hasNoRender && !isJobActive && !isJobFailed;

  const displayName = isNew
    ? `New #${sequenceNumber}`
    : title
      ? `#${sequenceNumber} ${title}`
      : name
        ? `#${sequenceNumber} ${name}`
        : `#${sequenceNumber}`;

  return (
    <AIGlowBorder
      isActive={activeRenderStatus === RenderStatus.PROCESSING}
      className="flex-shrink-0 rounded-lg overflow-hidden"
    >
      <Link
        href={`/project/${projectId}/concept/${concept.id}`}
        className="relative block group bg-muted/50"
        style={{
          height: PANEL_HEIGHT - 24,
          width: (PANEL_HEIGHT - 24) * CARD_ASPECT_RATIO,
        }}
      >
        {imageUrl ? (
          <>
            <Image
              src={imageUrl}
              alt={displayName}
              fill
              className={cn('object-cover', isDesaturated && 'saturate-[0.25] brightness-95 opacity-60')}
              sizes="200px"
            />
            <ConceptActivityOverlay isActive={isJobActive} isFailed={isJobFailed} t={t} />
          </>
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImagePlus className="h-6 w-6" />
            <span className="text-card-meta">Add Photo</span>
            <ConceptActivityOverlay isActive={isJobActive} isFailed={isJobFailed} t={t} />
          </div>
        )}

        {/* Bottom gradient + label + heart (mirrors Apple ConceptCard).
            Heart is always visible: filled/teal when favorite, outline/white
            otherwise — matches Apple `heart.fill` / `heart` SF symbol. */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 flex items-end gap-2">
          <span className="text-chip font-medium text-white truncate flex-1 min-w-0">
            {displayName}
          </span>
          {!hideOverlayBadges && (
            <Heart
              className={cn(
                'h-3.5 w-3.5 flex-shrink-0',
                isFavorite
                  ? 'text-primary fill-primary'
                  : 'text-white/70'
              )}
            />
          )}
        </div>
      </Link>
    </AIGlowBorder>
  );
}

// Project Concepts Section
interface ProjectConceptsSectionProps {
  concepts: (Concept | HistoryItem)[];
  isLoading: boolean;
  projectId: string;
  hasAngles: boolean;
  onUploadPhoto?: () => void;
  onCreateConcept?: () => void;
}

export function ProjectConceptsSection({
  concepts,
  isLoading,
  projectId,
  hasAngles,
  onUploadPhoto,
  onCreateConcept,
}: ProjectConceptsSectionProps) {
  const t = useTranslations('project');
  const tOnboarding = useTranslations('onboarding');

  const sortedConcepts = useMemo(() => {
    return [...concepts].sort((a, b) => {
      const seqA =
        (a.attributes as { project_sequence_number?: number })
          .project_sequence_number ?? 0;
      const seqB =
        (b.attributes as { project_sequence_number?: number })
          .project_sequence_number ?? 0;
      return seqB - seqA;
    });
  }, [concepts]);

  const lastConcept = sortedConcepts[0];
  const lastConceptIsEmpty =
    lastConcept &&
    !('main_angle_url' in lastConcept.attributes
      ? lastConcept.attributes.main_angle_url
      : 'image_url' in lastConcept.attributes
        ? lastConcept.attributes.image_url
        : null);

  const actions: SectionAction[] = [];
  actions.push({
    label: t('viewAllConcepts'),
    href: `/project/${projectId}/concepts`,
  });

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t('concepts')}
        actions={actions}
        count={concepts.length}
      />
      {isLoading ? (
        <SectionPanel>
          <div className="h-full flex items-center gap-2 p-3 overflow-hidden">
            <Skeleton className="h-full aspect-[4/3] rounded-lg flex-shrink-0" />
            <Skeleton className="h-full aspect-[4/3] rounded-lg flex-shrink-0" />
            <Skeleton className="h-full aspect-[4/3] rounded-lg flex-shrink-0" />
          </div>
        </SectionPanel>
      ) : sortedConcepts.length > 0 ? (
        <ScrollableCarousel>
          {sortedConcepts.map((concept, index) => (
            <ConceptThumbnail
              key={concept.id}
              concept={concept}
              projectId={projectId}
              isNew={index === 0 && lastConceptIsEmpty}
            />
          ))}
        </ScrollableCarousel>
      ) : hasAngles ? (
        <SectionPanel>
          <div className="flex h-full w-full items-center justify-center">
            <Button type="button" onClick={onCreateConcept}>
              <Plus className="h-4 w-4" />
              {t('createConcept')}
            </Button>
          </div>
        </SectionPanel>
      ) : (
        <SectionPanel>
          <button
            type="button"
            onClick={onUploadPhoto}
            className="block h-full w-full text-left"
          >
            <OnboardingEmptyState
              icon="concepts"
              step={4}
              description={tOnboarding('concepts')}
            />
          </button>
        </SectionPanel>
      )}
    </div>
  );
}
